/**
 * Calendar utility functions for generating ICS (iCalendar) files
 * RFC 5545 compliant
 */

/**
 * Escape text for ICS format (escape commas, semicolons, newlines, backslashes)
 */
const escapeICS = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
};

/**
 * Format date to ICS format (YYYYMMDDTHHmmssZ)
 */
const formatICSDate = (date: Date, allDay: boolean = false): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  
  if (allDay) {
    return `${year}${month}${day}`;
  }
  
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Generate unique ID for ICS event
 */
const generateUID = (prefix: string, id: string): string => {
  const timestamp = Date.now();
  return `${prefix}-${id}-${timestamp}@sciencepoints`;
};

/**
 * Download ICS file
 */
const downloadICS = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Sanitize filename (remove special characters)
 */
const sanitizeFilename = (text: string): string => {
  return text
    .replace(/[^a-z0-9]/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
};

export interface Task {
  id: string;
  title: string;
  description?: string | null;
  dueDate?: string | null;
  status?: string;
  project?: {
    title?: string;
  } | null;
  assignees?: Array<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>;
}

export interface Project {
  id: string;
  title: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: string;
  budget?: number | null;
  creator?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

/**
 * Generate ICS content for a task
 */
export const generateTaskICS = (task: Task, translations?: {
  taskEvent?: string;
  status?: string;
  assignees?: string;
  project?: string;
  noDate?: string;
}): string | null => {
  if (!task.dueDate) {
    return null;
  }

  const dueDate = new Date(task.dueDate);
  if (isNaN(dueDate.getTime())) {
    return null;
  }

  // Check if it's a date-only (no time component)
  const isAllDay = task.dueDate.length <= 10 || task.dueDate.indexOf('T') === -1;
  
  // For all-day events, use date-only format
  // For timed events, use full datetime format
  const dtStart = isAllDay 
    ? `DTSTART;VALUE=DATE:${formatICSDate(dueDate, true)}`
    : `DTSTART:${formatICSDate(dueDate, false)}`;
  
  // DTEND: same as DTSTART for all-day, or +1 hour for timed events
  let dtEnd: string;
  if (isAllDay) {
    const endDate = new Date(dueDate);
    endDate.setUTCDate(endDate.getUTCDate() + 1);
    dtEnd = `DTEND;VALUE=DATE:${formatICSDate(endDate, true)}`;
  } else {
    const endDate = new Date(dueDate);
    endDate.setUTCHours(endDate.getUTCHours() + 1);
    dtEnd = `DTEND:${formatICSDate(endDate, false)}`;
  }

  const summary = translations?.taskEvent 
    ? translations.taskEvent.replace('{{title}}', task.title)
    : `Task: ${task.title}`;
  
  const descriptionParts: string[] = [];
  if (task.description) {
    descriptionParts.push(escapeICS(task.description));
  }
  if (task.status) {
    const statusLabel = translations?.status || 'Status';
    descriptionParts.push(`${statusLabel}: ${task.status}`);
  }
  if (task.project?.title) {
    const projectLabel = translations?.project || 'Project';
    descriptionParts.push(`${projectLabel}: ${task.project.title}`);
  }
  if (task.assignees && task.assignees.length > 0) {
    const assigneesLabel = translations?.assignees || 'Assignees';
    const assigneeNames = task.assignees
      .map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || '')
      .filter(Boolean)
      .join(', ');
    if (assigneeNames) {
      descriptionParts.push(`${assigneesLabel}: ${assigneeNames}`);
    }
  }

  const description = descriptionParts.length > 0 
    ? descriptionParts.join('\\n')
    : summary;

  const uid = generateUID('task', task.id);
  const dtStamp = formatICSDate(new Date(), false);

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SciencePoints//Task Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    dtStart,
    dtEnd,
    `DTSTAMP:${dtStamp}`,
    `SUMMARY:${escapeICS(summary)}`,
    `DESCRIPTION:${escapeICS(description)}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
};

/**
 * Generate ICS content for a project
 */
export const generateProjectICS = (project: Project, translations?: {
  projectEvent?: string;
  status?: string;
  budget?: string;
  creator?: string;
  noDate?: string;
}): string | null => {
  // Need at least startDate or endDate
  if (!project.startDate && !project.endDate) {
    return null;
  }

  const startDate = project.startDate ? new Date(project.startDate) : null;
  const endDate = project.endDate ? new Date(project.endDate) : null;

  // Use startDate if available, otherwise use endDate
  const eventStart = startDate || endDate;
  if (!eventStart || isNaN(eventStart.getTime())) {
    return null;
  }

  // Determine if all-day (date-only format)
  const isAllDay = project.startDate 
    ? (project.startDate.length <= 10 || project.startDate.indexOf('T') === -1)
    : (project.endDate!.length <= 10 || project.endDate!.indexOf('T') === -1);

  // DTSTART
  const dtStart = isAllDay
    ? `DTSTART;VALUE=DATE:${formatICSDate(eventStart, true)}`
    : `DTSTART:${formatICSDate(eventStart, false)}`;

  // DTEND: use endDate if available, otherwise startDate + 1 day
  let dtEnd: string;
  if (endDate && !isNaN(endDate.getTime())) {
    if (isAllDay) {
      const nextDay = new Date(endDate);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      dtEnd = `DTEND;VALUE=DATE:${formatICSDate(nextDay, true)}`;
    } else {
      dtEnd = `DTEND:${formatICSDate(endDate, false)}`;
    }
  } else {
    // No end date, use start + 1 day
    const nextDay = new Date(eventStart);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    dtEnd = isAllDay
      ? `DTEND;VALUE=DATE:${formatICSDate(nextDay, true)}`
      : `DTEND:${formatICSDate(nextDay, false)}`;
  }

  const summary = translations?.projectEvent
    ? translations.projectEvent.replace('{{title}}', project.title)
    : `Project: ${project.title}`;

  const descriptionParts: string[] = [];
  if (project.description) {
    descriptionParts.push(escapeICS(project.description));
  }
  if (project.status) {
    const statusLabel = translations?.status || 'Status';
    descriptionParts.push(`${statusLabel}: ${project.status}`);
  }
  if (project.budget !== null && project.budget !== undefined) {
    const budgetLabel = translations?.budget || 'Budget';
    descriptionParts.push(`${budgetLabel}: ${project.budget}`);
  }
  if (project.creator) {
    const creatorLabel = translations?.creator || 'Creator';
    const creatorName = `${project.creator.firstName || ''} ${project.creator.lastName || ''}`.trim() || project.creator.email || '';
    if (creatorName) {
      descriptionParts.push(`${creatorLabel}: ${creatorName}`);
    }
  }

  const description = descriptionParts.length > 0
    ? descriptionParts.join('\\n')
    : summary;

  const uid = generateUID('project', project.id);
  const dtStamp = formatICSDate(new Date(), false);

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SciencePoints//Project Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    dtStart,
    dtEnd,
    `DTSTAMP:${dtStamp}`,
    `SUMMARY:${escapeICS(summary)}`,
    `DESCRIPTION:${escapeICS(description)}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
};

/**
 * Download task ICS file
 */
export const downloadTaskICS = (task: Task, translations?: {
  taskEvent?: string;
  status?: string;
  assignees?: string;
  project?: string;
  noDate?: string;
}): boolean => {
  const icsContent = generateTaskICS(task, translations);
  if (!icsContent) {
    return false;
  }

  const dateStr = task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : 'no-date';
  const filename = `task-${sanitizeFilename(task.title)}-${dateStr}.ics`;
  downloadICS(icsContent, filename);
  return true;
};

/**
 * Download project ICS file
 */
export const downloadProjectICS = (project: Project, translations?: {
  projectEvent?: string;
  status?: string;
  budget?: string;
  creator?: string;
  noDate?: string;
}): boolean => {
  const icsContent = generateProjectICS(project, translations);
  if (!icsContent) {
    return false;
  }

  const dateStr = project.startDate 
    ? new Date(project.startDate).toISOString().split('T')[0]
    : (project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : 'no-date');
  const filename = `project-${sanitizeFilename(project.title)}-${dateStr}.ics`;
  downloadICS(icsContent, filename);
  return true;
};

/**
 * Format date for Google Calendar (YYYYMMDDTHHmmssZ)
 */
const formatGoogleCalendarDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Generate Outlook Calendar URL for a task
 */
export const generateOutlookTaskURL = (task: Task, translations?: {
  taskEvent?: string;
}): string | null => {
  if (!task.dueDate) {
    return null;
  }

  const dueDate = new Date(task.dueDate);
  if (isNaN(dueDate.getTime())) {
    return null;
  }

  const isAllDay = task.dueDate.length <= 10 || task.dueDate.indexOf('T') === -1;
  
  // For all-day events, set to start of day and end next day
  let startDate: Date;
  let endDate: Date;
  
  if (isAllDay) {
    startDate = new Date(dueDate);
    startDate.setUTCHours(0, 0, 0, 0);
    endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + 1);
  } else {
    startDate = new Date(dueDate);
    endDate = new Date(startDate);
    endDate.setUTCHours(endDate.getUTCHours() + 1);
  }

  const summary = translations?.taskEvent 
    ? translations.taskEvent.replace('{{title}}', task.title)
    : `Task: ${task.title}`;

  const descriptionParts: string[] = [];
  if (task.description) {
    descriptionParts.push(task.description);
  }
  if (task.status) {
    descriptionParts.push(`Status: ${task.status}`);
  }
  if (task.project?.title) {
    descriptionParts.push(`Project: ${task.project.title}`);
  }
  if (task.assignees && task.assignees.length > 0) {
    const assigneeNames = task.assignees
      .map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || '')
      .filter(Boolean)
      .join(', ');
    if (assigneeNames) {
      descriptionParts.push(`Assignees: ${assigneeNames}`);
    }
  }

  const description = descriptionParts.length > 0 ? descriptionParts.join('\n\n') : summary;

  const params = new URLSearchParams({
    subject: summary,
    body: description,
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString()
  });

  return `https://outlook.office.com/calendar/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Google Calendar URL for a task
 */
export const generateGoogleTaskURL = (task: Task, translations?: {
  taskEvent?: string;
}): string | null => {
  if (!task.dueDate) {
    return null;
  }

  const dueDate = new Date(task.dueDate);
  if (isNaN(dueDate.getTime())) {
    return null;
  }

  const isAllDay = task.dueDate.length <= 10 || task.dueDate.indexOf('T') === -1;
  
  let startDate: Date;
  let endDate: Date;
  
  if (isAllDay) {
    startDate = new Date(dueDate);
    startDate.setUTCHours(0, 0, 0, 0);
    endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + 1);
  } else {
    startDate = new Date(dueDate);
    endDate = new Date(startDate);
    endDate.setUTCHours(endDate.getUTCHours() + 1);
  }

  const summary = translations?.taskEvent 
    ? translations.taskEvent.replace('{{title}}', task.title)
    : `Task: ${task.title}`;

  const descriptionParts: string[] = [];
  if (task.description) {
    descriptionParts.push(task.description);
  }
  if (task.status) {
    descriptionParts.push(`Status: ${task.status}`);
  }
  if (task.project?.title) {
    descriptionParts.push(`Project: ${task.project.title}`);
  }
  if (task.assignees && task.assignees.length > 0) {
    const assigneeNames = task.assignees
      .map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || '')
      .filter(Boolean)
      .join(', ');
    if (assigneeNames) {
      descriptionParts.push(`Assignees: ${assigneeNames}`);
    }
  }

  const description = descriptionParts.length > 0 ? descriptionParts.join('\n\n') : summary;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: summary,
    dates: `${formatGoogleCalendarDate(startDate)}/${formatGoogleCalendarDate(endDate)}`,
    details: description
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL for a project
 */
export const generateOutlookProjectURL = (project: Project, translations?: {
  projectEvent?: string;
}): string | null => {
  if (!project.startDate && !project.endDate) {
    return null;
  }

  const startDate = project.startDate ? new Date(project.startDate) : null;
  const endDate = project.endDate ? new Date(project.endDate) : null;

  const eventStart = startDate || endDate;
  if (!eventStart || isNaN(eventStart.getTime())) {
    return null;
  }

  const isAllDay = project.startDate 
    ? (project.startDate.length <= 10 || project.startDate.indexOf('T') === -1)
    : (project.endDate!.length <= 10 || project.endDate!.indexOf('T') === -1);

  let eventStartDate: Date;
  let eventEndDate: Date;

  if (isAllDay) {
    eventStartDate = new Date(eventStart);
    eventStartDate.setUTCHours(0, 0, 0, 0);
    if (endDate && !isNaN(endDate.getTime())) {
      eventEndDate = new Date(endDate);
      eventEndDate.setUTCHours(0, 0, 0, 0);
      eventEndDate.setUTCDate(eventEndDate.getUTCDate() + 1);
    } else {
      eventEndDate = new Date(eventStartDate);
      eventEndDate.setUTCDate(eventEndDate.getUTCDate() + 1);
    }
  } else {
    eventStartDate = new Date(eventStart);
    if (endDate && !isNaN(endDate.getTime())) {
      eventEndDate = new Date(endDate);
    } else {
      eventEndDate = new Date(eventStartDate);
      eventEndDate.setUTCHours(eventEndDate.getUTCHours() + 1);
    }
  }

  const summary = translations?.projectEvent
    ? translations.projectEvent.replace('{{title}}', project.title)
    : `Project: ${project.title}`;

  const descriptionParts: string[] = [];
  if (project.description) {
    descriptionParts.push(project.description);
  }
  if (project.status) {
    descriptionParts.push(`Status: ${project.status}`);
  }
  if (project.budget !== null && project.budget !== undefined) {
    descriptionParts.push(`Budget: ${project.budget}`);
  }
  if (project.creator) {
    const creatorName = `${project.creator.firstName || ''} ${project.creator.lastName || ''}`.trim() || project.creator.email || '';
    if (creatorName) {
      descriptionParts.push(`Creator: ${creatorName}`);
    }
  }

  const description = descriptionParts.length > 0 ? descriptionParts.join('\n\n') : summary;

  const params = new URLSearchParams({
    subject: summary,
    body: description,
    startdt: eventStartDate.toISOString(),
    enddt: eventEndDate.toISOString()
  });

  return `https://outlook.office.com/calendar/deeplink/compose?${params.toString()}`;
};

/**
 * Generate Google Calendar URL for a project
 */
export const generateGoogleProjectURL = (project: Project, translations?: {
  projectEvent?: string;
}): string | null => {
  if (!project.startDate && !project.endDate) {
    return null;
  }

  const startDate = project.startDate ? new Date(project.startDate) : null;
  const endDate = project.endDate ? new Date(project.endDate) : null;

  const eventStart = startDate || endDate;
  if (!eventStart || isNaN(eventStart.getTime())) {
    return null;
  }

  const isAllDay = project.startDate 
    ? (project.startDate.length <= 10 || project.startDate.indexOf('T') === -1)
    : (project.endDate!.length <= 10 || project.endDate!.indexOf('T') === -1);

  let eventStartDate: Date;
  let eventEndDate: Date;

  if (isAllDay) {
    eventStartDate = new Date(eventStart);
    eventStartDate.setUTCHours(0, 0, 0, 0);
    if (endDate && !isNaN(endDate.getTime())) {
      eventEndDate = new Date(endDate);
      eventEndDate.setUTCHours(0, 0, 0, 0);
      eventEndDate.setUTCDate(eventEndDate.getUTCDate() + 1);
    } else {
      eventEndDate = new Date(eventStartDate);
      eventEndDate.setUTCDate(eventEndDate.getUTCDate() + 1);
    }
  } else {
    eventStartDate = new Date(eventStart);
    if (endDate && !isNaN(endDate.getTime())) {
      eventEndDate = new Date(endDate);
    } else {
      eventEndDate = new Date(eventStartDate);
      eventEndDate.setUTCHours(eventEndDate.getUTCHours() + 1);
    }
  }

  const summary = translations?.projectEvent
    ? translations.projectEvent.replace('{{title}}', project.title)
    : `Project: ${project.title}`;

  const descriptionParts: string[] = [];
  if (project.description) {
    descriptionParts.push(project.description);
  }
  if (project.status) {
    descriptionParts.push(`Status: ${project.status}`);
  }
  if (project.budget !== null && project.budget !== undefined) {
    descriptionParts.push(`Budget: ${project.budget}`);
  }
  if (project.creator) {
    const creatorName = `${project.creator.firstName || ''} ${project.creator.lastName || ''}`.trim() || project.creator.email || '';
    if (creatorName) {
      descriptionParts.push(`Creator: ${creatorName}`);
    }
  }

  const description = descriptionParts.length > 0 ? descriptionParts.join('\n\n') : summary;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: summary,
    dates: `${formatGoogleCalendarDate(eventStartDate)}/${formatGoogleCalendarDate(eventEndDate)}`,
    details: description
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// --- Reservation (Stiklainis) ---

export interface Reservation {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  room: { id: string; name: string; capacity?: number; location?: string };
  user: { id?: string; firstName: string; lastName: string; department?: string };
}

export interface ReservationTranslations {
  reservationEvent?: string;
  room?: string;
  organizer?: string;
}

/**
 * Generate ICS content for a reservation
 */
export const generateReservationICS = (
  reservation: Reservation,
  translations?: ReservationTranslations
): string | null => {
  const start = new Date(reservation.startTime);
  const end = new Date(reservation.endTime);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return null;
  }

  const dtStart = `DTSTART:${formatICSDate(start, false)}`;
  const dtEnd = `DTEND:${formatICSDate(end, false)}`;

  const summary = translations?.reservationEvent
    ? translations.reservationEvent.replace('{{title}}', reservation.title)
    : `Reservation: ${reservation.title}`;

  const organizerLabel = translations?.organizer || 'Organizatorius';
  const organizerName = reservation.user
    ? `${reservation.user.firstName || ''} ${reservation.user.lastName || ''}`.trim()
    : '';
  const parts: string[] = [];
  if (reservation.description?.trim()) {
    parts.push(reservation.description.trim());
  }
  if (organizerName) {
    if (parts.length > 0) parts.push('');
    parts.push(`${organizerLabel}: ${organizerName}`);
  }
  const descriptionText = parts.length > 0 ? parts.join('\n\n') : summary;
  const description = escapeICS(descriptionText);

  const uid = generateUID('reservation', reservation.id);
  const dtStamp = formatICSDate(new Date(), false);

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SciencePoints//Reservation Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    dtStart,
    dtEnd,
    `DTSTAMP:${dtStamp}`,
    `SUMMARY:${escapeICS(summary)}`,
    `DESCRIPTION:${description}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
};

/**
 * Download reservation ICS file
 */
export const downloadReservationICS = (
  reservation: Reservation,
  translations?: ReservationTranslations
): boolean => {
  const icsContent = generateReservationICS(reservation, translations);
  if (!icsContent) return false;

  const dateStr = new Date(reservation.startTime).toISOString().split('T')[0];
  const filename = `reservation-${sanitizeFilename(reservation.title)}-${dateStr}.ics`;
  downloadICS(icsContent, filename);
  return true;
};

/**
 * Generate Outlook Calendar URL for a reservation
 */
export const generateOutlookReservationURL = (
  reservation: Reservation,
  translations?: ReservationTranslations
): string | null => {
  const start = new Date(reservation.startTime);
  const end = new Date(reservation.endTime);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;  const summary = translations?.reservationEvent
    ? translations.reservationEvent.replace('{{title}}', reservation.title)
    : `Reservation: ${reservation.title}`;

  const organizerLabel = translations?.organizer || 'Organizatorius';
  const organizerName = reservation.user
    ? `${reservation.user.firstName || ''} ${reservation.user.lastName || ''}`.trim()
    : '';
  const parts: string[] = [];
  if (reservation.description?.trim()) {
    parts.push(reservation.description.trim());
  }
  if (organizerName) {
    if (parts.length > 0) parts.push('');
    parts.push(`${organizerLabel}: ${organizerName}`);
  }
  const description = parts.length > 0 ? parts.join('\n\n') : summary;

  // Outlook deeplink: use explicit CRLF (%0D%0A) for line breaks - URLSearchParams may not preserve formatting
  const bodyEncoded = encodeURIComponent(description.replace(/\n/g, '\r\n'));
  const params = new URLSearchParams({
    subject: summary,
    startdt: start.toISOString(),
    enddt: end.toISOString()
  });
  return `https://outlook.office.com/calendar/deeplink/compose?${params.toString()}&body=${bodyEncoded}`;
};

/**
 * Generate Google Calendar URL for a reservation
 */
export const generateGoogleReservationURL = (
  reservation: Reservation,
  translations?: ReservationTranslations
): string | null => {
  const start = new Date(reservation.startTime);
  const end = new Date(reservation.endTime);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const summary = translations?.reservationEvent
    ? translations.reservationEvent.replace('{{title}}', reservation.title)
    : `Reservation: ${reservation.title}`;

  const organizerLabel = translations?.organizer || 'Organizatorius';
  const organizerName = reservation.user
    ? `${reservation.user.firstName || ''} ${reservation.user.lastName || ''}`.trim()
    : '';
  const parts: string[] = [];
  if (reservation.description?.trim()) {
    parts.push(reservation.description.trim());
  }
  if (organizerName) {
    if (parts.length > 0) parts.push('');
    parts.push(`${organizerLabel}: ${organizerName}`);
  }
  const description = parts.length > 0 ? parts.join('\n\n') : summary;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: summary,
    dates: `${formatGoogleCalendarDate(start)}/${formatGoogleCalendarDate(end)}`,
    details: description
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
